import {Component, Input, OnInit} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {BucketName, IncidentApiService, IncidentResponse, InquiriesResponse, ReferenceBookResponse} from '@npck/af-frontend-api';
import {rxSpinner} from '@npck/libs/commons';
import {AttachmentsForm, createAttachmentsRequest} from '../../../../components/files/files.component';
import {AuthHolderService} from '../../../../domain/auth-holder.service';
import {DESCRIPTION_MAX_LENGTH} from './inquiry-response-panel/inquiry-response-panel.component';

const NPCK_BIN = '960440000151';

@Component({
    selector: 'af-incident-inquiries-tab',
    templateUrl: './incident-inquiry-tab.component.html',
    styleUrl: './incident-inquiries-tab.component.scss',
    standalone: false
})
export class IncidentInquiriesTabComponent implements OnInit {
    @Input() incident: IncidentResponse;
    @Input() inquiriesResponse: InquiriesResponse[];
    DESCRIPTION_MAX_LENGTH = DESCRIPTION_MAX_LENGTH;
    form: FormGroup<CreateIncidentForm>;
    isInquiryPanelOpened: boolean;
    inquiriesResponseViewModels: InquiriesResponseViewModel[] = [];
    isAvailableToScroll: boolean;
    filterDictionaryBy = [NPCK_BIN];
    BucketName = BucketName;

    constructor(
        public authHolderService: AuthHolderService,
        private incidentApiService: IncidentApiService,
        private formBuilder: FormBuilder,
    ) {
    }

    ngOnInit(): void {
        if (this.incident.governmentBin) {
            this.filterDictionaryBy.push(this.incident.governmentBin);
        }
        this.filterDictionaryBy.push(...this.incident.related);
        this.incident.source_organization && this.filterDictionaryBy.push(this.incident.source_organization);

        this.form = this.formBuilder.group({
            organization: new FormControl({value: null, disabled: true}, [Validators.required]),
            description: new FormControl(null, [Validators.required, Validators.maxLength(DESCRIPTION_MAX_LENGTH)]),
            files: this.formBuilder.group({}),
        });
        this.mapToViewModel(this.inquiriesResponse);
    }

    updateInquiries(): void {
        this.incidentApiService.getIncidentInquiries(this.incident.id)
            .pipe(rxSpinner('submit'))
            .subscribe(response => this.mapToViewModel(response));
    }

    createInquiry(): void {
        this.incidentApiService.sendInquiry(this.incident.id, {
            bin: this.form.value.organization,
            additionalData: {
                description: this.form.value.description,
                attachments: createAttachmentsRequest(this.form.controls.files.value),
            },
        })
            .pipe(rxSpinner('submit'))
            .subscribe(() => {
                this.form.reset();
                this.form.updateValueAndValidity();
                this.isInquiryPanelOpened = false;
                this.updateInquiries();
            });
    }

    onOrganizationsLoaded(dict: ReferenceBookResponse[]): void {
        if (dict?.length) {
            this.form.controls.organization.enable();
        } else {
            this.form.controls.organization.disable();
        }
    }

    private mapToViewModel(inquiriesResponse: InquiriesResponse[]): void {
        if (!inquiriesResponse.length) {
            return;
        }
        this.inquiriesResponseViewModels = inquiriesResponse.map(item => <InquiriesResponseViewModel>{
            ...item,
            isToday: this.isToday(item.createdDate),
            hasUnreadInquiries: item.items.some(groupedByDate => groupedByDate.inquiry.isUnread || groupedByDate.response?.isUnread),
        });
        this.isAvailableToScroll = this.inquiriesResponseViewModels.map(item => item.items)
            .reduce((acc, val) => acc.concat(val), [])
            .reduce((acc, val) => acc + (val.inquiry ? 1 : 0) + (val.response ? 1 : 0), 0) >= 3;
    }

    private isToday(dateString: string): boolean {
        const inputDate = new Date(dateString);
        const today = new Date();

        return (
            inputDate.getUTCFullYear() === today.getUTCFullYear() &&
            inputDate.getUTCMonth() === today.getUTCMonth() &&
            inputDate.getUTCDate() === today.getUTCDate()
        );
    }
}

interface CreateIncidentForm {
    organization: FormControl<string>;
    description: FormControl<string>;
    files: FormGroup<AttachmentsForm>;
}

interface InquiriesResponseViewModel extends InquiriesResponse {
    hasUnreadInquiries: boolean;
    isToday: boolean;
}
